import { IonItem, IonLabel, IonToggle, useIonToast } from "@ionic/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  isBiometricAvailable,
  isBiometricEnabled,
  promptBiometric,
  setBiometricEnabled,
} from "../util/Biometric";
import { persistBiometricCredentialForEnrollment } from "../util/OAuth";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";

/**
 * Settings toggle to enable/disable biometric (Face ID / Touch ID / fingerprint)
 * sign-in. Renders nothing on devices/platforms without biometric support
 * (including web/PWA), so the standard B2C login is used there.
 */
const BiometricToggle: React.FC = () => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);
  const [presentToast] = useIonToast();
  const [available, setAvailable] = useState(false);
  const [label, setLabel] = useState("Biometric");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const availability = await isBiometricAvailable();
      const currentlyEnabled = await isBiometricEnabled();
      if (!mounted) return;
      setAvailable(availability.available);
      setLabel(availability.label);
      setEnabled(currentlyEnabled);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = useCallback(async () => {
    if (!enabled) {
      //enabling - confirm with a biometric prompt, then store the durable credential
      const verified = await promptBiometric(`Enable ${label} sign-in`);
      if (!verified) {
        return;
      }
      await setBiometricEnabled(true);
      const stored = await persistBiometricCredentialForEnrollment();
      if (!stored) {
        await setBiometricEnabled(false);
        presentToast({
          message:
            translatedMessages["BiometricNoSession"]?.MessageText ||
            "Unable to enable sign-in. Please try again.",
          duration: 1500,
          position: "top",
        });
        return;
      }
      setEnabled(true);
      presentToast({
        message:
          translatedMessages["BiometricEnabled"]?.MessageText ||
          `${label} sign-in enabled`,
        duration: 1500,
        position: "top",
      });
    } else {
      //disabling - clears the stored credential
      await setBiometricEnabled(false);
      setEnabled(false);
      presentToast({
        message:
          translatedMessages["BiometricDisabled"]?.MessageText ||
          `${label} sign-in disabled`,
        duration: 1500,
        position: "top",
      });
    }
  }, [enabled, label, presentToast, translatedMessages]);

  if (!available) {
    return null;
  }

  return (
    <IonItem>
      <IonLabel className="ion-text-wrap">
        <h2>{`${label} ${translations["lbl_mob_sign_in"] || "sign-in"}`}</h2>
        <p>
          {translatedMessages["BiometricDescription"]?.MessageText ||
            `Use ${label} to sign in without entering your credentials each time.`}
        </p>
      </IonLabel>
      <IonToggle
        slot="end"
        checked={enabled}
        onClick={handleToggle}
        aria-label={`${label} sign-in`}
      />
    </IonItem>
  );
};

export default BiometricToggle;
